import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import LoadingOverlay from "react-loading-overlay";
import { shallowEqual, useSelector } from "react-redux";
import * as Yup from "yup";
import Cookies from "js-cookie";
import {
  FieldsGenerator,
  FormButton,
  phoneRegExp,
  emailValidationRegExp,
  nameRegExp,
  validateForm,
  handleChange,
  handleValidateFields,
  PageSection,
} from "../Common/formfields";
import { UserFields, UserAddressFields, FomFields } from "./userfields";
import * as manageuserConsts from "../Common/usermanagementconst";
import { consturctAddressState } from "../../../components/VendorOnboarding/Views/Address/helpers";
import ErrorContainer from "../../Common/Errors/ErrorContainer";
import {
  httpGetManageUserValidateEmail,
  httpPostManageUserAddUser,
} from "../../../service/httpClient";
import * as _ from "../../../constants/Constants";

export function CreateUserForm() {
  const store = useSelector(
    ({ service, common }) => ({
      vendorProfile: service.vendorProfile,
      profileId: common.profileId,
      vendorCorpName: common.vendorCorpName,
    }),
    shallowEqual
  );

  const {
    mailAddress: dbPrimaryAddress,
    primaryContact: dbPrimaryContact,
    vendorDetail,
  } = store?.vendorProfile || "";

  const initialState = {
    ...consturctAddressState({}),
    vendorProfileId: store?.profileId,
    firstName: "",
    lastName: "",
    email: "",
    locale: manageuserConsts.DEFAULT_LOCALE,
    availableRoles: null,
    enterpriseName: store?.vendorCorpName || "",
    vmsFOMRoles: manageuserConsts.DEFAULT_VMS_ROLE,
    phone: dbPrimaryContact?.phoneNumber || "",
    primaryAddress: {
      streetAddress: dbPrimaryAddress?.streetAddress || "",
      suiteUnitFloor: dbPrimaryAddress?.suiteUnitFloor || "",
      city: dbPrimaryAddress?.city || "",
      state: dbPrimaryAddress?.state || "",
      zipCode: dbPrimaryAddress?.zipCode || "",
      country: dbPrimaryAddress?.country || "",
    },
    isActive: true,
    primaryUser: false,
    loggedInUser: Cookies.get("email"),
    errors: {},
  };
  const appsAllowedAccessToVendor =
    vendorDetail?.merchandiseType !== _.VOB_MERCHANDISE_TYPE_MARKETPLACE
      ? ["fom"]
      : []; //"fom", "seller"//Will be dynamic from DB
  const [disableSave, setDisableSave] = useState(true);
  const [disableSaveDynamicFlds, setDisableSaveDynamicFlds] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    ...initialState,
    prev: initialState,
  });
  const [frmErrSuccessStatus, setFrmErrSuccessStatus] = useState({
    updateStatus: "",
    successMsg: "",
    errorStatus: "",
    errorMsg: "",
  });

  const handleSuccessErrorMsgs = (statusType, msg) => {
    if (statusType === "init") {
      setFrmErrSuccessStatus({
        updateStatus: false,
        successMsg: "",
        errorStatus: false,
        errorMsg: "",
      });
    } else if (statusType === "success") {
      setFrmErrSuccessStatus({
        updateStatus: true,
        successMsg: msg,
        errorStatus: "",
        errorMsg: "",
      });
    } else {
      setFrmErrSuccessStatus({
        updateStatus: "",
        successMsg: "",
        errorStatus: true,
        errorMsg: msg,
      });
    }
  };

  useEffect(() => {
    //Check if vendor selected
    if (Object.keys(store?.vendorProfile).length === 0) {
      handleSuccessErrorMsgs(
        "error",
        manageuserConsts.MSG_MANAGE_USERS_SELECT_VENDOR
      );
    }
  }, []);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "Need at least 2 characters.")
      .matches(
        nameRegExp,
        "The first name may only contain letters, space, and special characters."
      )
      .required("First Name is Required."),
    lastName: Yup.string()
      .min(2, "Need at least 2 characters.")
      .matches(
        nameRegExp,
        "The last name may only contain letters, space, and special characters."
      )
      .required("Last Name is Required."),
    email: Yup.string()
      .email("Email is not valid.")
      .min(5, "Must be more than 5 characters.")
      .matches(emailValidationRegExp, "Email is not valid.")
      .required("Email is Required.")
      .test(
        "internal-user-validation",
        manageuserConsts.RESTRICT_INTERNAL_USER_EMAIL,
        (value) =>
          !(
            value?.includes("@macys.com") ||
            value?.includes("@bloomingdales.com")
          )
      ),
    phone: Yup.string()
      .min(10, "Phone number should be 10 characters long.")
      .matches(phoneRegExp, "Phone number is not valid.")
      .notRequired(""),
    locale: Yup.string().notRequired(""),
    vmsFOMRoles: Yup.string().required("VMS Role field is Required."),
    enterpriseName: Yup.string().notRequired(""),
    availableRoles: Yup.string().required(
      "VSP Available Roles field is required."
    ),
  });

  const handleSave = () => {
    setDisableSave(true);
    setIsLoading(true);
    if (validateForm({ fieldsObj: UserFields, formValues: formValues })) {
      //Validate Email to be unique
      let encodedEmail = encodeURIComponent(formValues.email);
      httpGetManageUserValidateEmail(encodedEmail)
        .then((res) => {
          const { email_exist } = res?.data || false;
          if (!email_exist) {
            //Save API call payload
            let payloadCreateUser = {
              vendorProfileId: formValues.vendorProfileId,
              firstName: formValues.firstName,
              lastName: formValues.lastName,
              locale: formValues.locale,
              applicationRole: formValues.availableRoles,
              email: formValues.email,
              vmsRole: formValues.vmsFOMRoles,
              createdBy: formValues.loggedInUser,
              isActive: formValues.isActive,
              primaryUser: formValues.primaryUser,
              vendorCorpName: store.vendorCorpName,
            };

            httpPostManageUserAddUser(payloadCreateUser)
              .then((res) => {
                const { message, userStatus } = res?.data || false;
                if (
                  ["complete", "in_process"].includes(userStatus) &&
                  (res.status === 200 || res.status === 201)
                ) {
                  handleSuccessErrorMsgs(
                    "success",
                    message ||
                      manageuserConsts.MSG_MANAGE_USERS_CREATE_USER_SUCCESS
                  );
                  setFormValues(initialState);
                } else {
                  handleSuccessErrorMsgs(
                    "error",
                    message ||
                      manageuserConsts.MSG_MANAGE_USERS_CREATE_USER_FAIL
                  );
                }
                setIsLoading(false);
              })
              .catch((error) => {
                handleSuccessErrorMsgs(
                  "error",
                  manageuserConsts.MSG_MANAGE_USERS_CREATE_USER_FAIL
                );
                setIsLoading(false);
              });
          } else {
            handleSuccessErrorMsgs(
              "error",
              manageuserConsts.MSG_ERROR_EMAIL_ALREADY_EXISTS
            );
            setIsLoading(false);
          }
        })
        .catch((error) => {
          handleSuccessErrorMsgs(
            "error",
            manageuserConsts.MSG_ERROR_VALIDATE_EMAIL
          );
          setIsLoading(false);
        });
    }
  };

  const DynamicFields = () => {
    return (
      <>
        {appsAllowedAccessToVendor.includes("fom") && (
          <>
            <PageSection title={manageuserConsts.VSP_DETAILS} />
            <div className="form-container row-fields-container">
              <div className="mb-4 form-row">
                {FomFields.map((value, index) => {
                  return (
                    <FieldsGenerator
                      fieldObj={value}
                      key={index}
                      formValues={formValues}
                      setFormValues={setFormValues}
                      handleValidateFields={handleValidateFields}
                      handleChange={handleChange}
                      setDisableSave={setDisableSaveDynamicFlds}
                      allFieldsObj={FomFields}
                      validationSchema={validationSchema}
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}
      </>
    );
  };
  return (
    <LoadingOverlay active={isLoading} spinner>
      <div className="form-master-container">
        <div className="successErrorContainer">
          {(frmErrSuccessStatus?.updateStatus ||
            frmErrSuccessStatus?.errorStatus) && (
            <ErrorContainer
              updateStatus={frmErrSuccessStatus?.updateStatus}
              successMsg={frmErrSuccessStatus?.successMsg}
              errorMsg={frmErrSuccessStatus?.errorMsg}
              errorStatus={frmErrSuccessStatus?.errorStatus}
              errorStyle="errorMessage"
              successStyle="successMessage"
            />
          )}
        </div>

        <PageSection title={manageuserConsts.BASIC_DETAILS} />
        <Form className="form-container">
          {UserFields.map((value, index) => {
            return (
              <FieldsGenerator
                fieldObj={value}
                key={index}
                formValues={formValues}
                setFormValues={setFormValues}
                handleValidateFields={handleValidateFields}
                handleChange={handleChange}
                setDisableSave={setDisableSave}
                allFieldsObj={UserFields}
                validationSchema={validationSchema}
              />
            );
          })}
        </Form>

        <PageSection title={manageuserConsts.ADDRESS_DETAILS} />
        <div className="form-container row-fields-container">
          {UserAddressFields.map((value, index) => {
            return (
              <FieldsGenerator
                fieldObj={value}
                key={index}
                formValues={formValues}
                setFormValues={setFormValues}
                handleChange={handleChange}
              />
            );
          })}
        </div>

        <DynamicFields />
        <div className="row-fields-container">
          <FormButton
            label={manageuserConsts.CREATE_USER}
            parentElemCls="row-btn-container"
            disabled={
              disableSave ||
              (vendorDetail?.merchandiseType !==
                _.VOB_MERCHANDISE_TYPE_MARKETPLACE &&
                disableSaveDynamicFlds)
            }
            onClick={handleSave}
          />
        </div>
      </div>
    </LoadingOverlay>
  );
}
